import React from 'react';
import { Box, Divider, Menu, MenuItem, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  ArrowLeftRight,
  Bell,
  CreditCard,
  GraduationCap,
  Landmark,
  LogOut,
  LucideIcon,
  PiggyBank,
} from 'lucide-react';
import useUser from '../../hooks/global/useUser';
import { LoadedUser } from '../../interfaces';
import useGetSubscription from '../../hooks/subscriptions/useGetSubscription';
import useGetOnboarding from '../../hooks/businesses/useGetOnboarding';
import { ROUTES } from '../../constants/global';
import { LEARN_IS_HIDDEN } from '../../constants/learn';
import { isSudoManager } from '../../utils/global/helper';
import { handleSignOut } from '../../helpers/global';
import { userHasAccessToCapital } from '../../helpers/capital';

interface DropdownNavMenuProps {
  anchorElUser: null | HTMLElement;
  handleCloseDropdown: () => void;
  setMobileDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Route {
  id: string;
  text: string;
  to: string;
  icon: LucideIcon;
  visible: boolean;
}

const businessInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();

const menuItemSx = {
  height: 36,
  gap: 1.375,
  px: 2,
  typography: 'subtitle2',
  color: 'text.primary',
};

// eslint-disable-next-line no-restricted-syntax -- typography-exception: lucide icon props take a CSS colour, not a palette path (5.1:1 on white)
const menuIconProps = { size: 19, strokeWidth: 1.75, color: '#6E6E6E' };

function DropdownNavMenu({
  anchorElUser,
  handleCloseDropdown,
  setMobileDrawerOpen,
}: DropdownNavMenuProps) {
  const { user } = useUser<LoadedUser>();
  const { data: subData } = useGetSubscription();
  const { selectBusiness } = useUser();
  const { isEnabled: isOnboardingAudience, isLoading: isOnboardingLoading } =
    useGetOnboarding();

  const isIndependent = user.business.type === 'INDEPENDENT';
  const isPaymentsManager =
    user.business.type === 'STUDIO' && isSudoManager(user);
  const isSubscriptionManager = isIndependent || isSudoManager(user);

  const routeGroups: Route[][] = [
    [
      {
        id: 'subscription',
        text: 'Manage Subscription',
        to: ROUTES.subscription,
        icon: CreditCard,
        visible: isSubscriptionManager && !!subData?.subscription?.id,
      },
      {
        id: 'payments',
        text: 'Payments Account',
        to: ROUTES.payments,
        icon: Landmark,
        visible: isPaymentsManager,
      },
      {
        id: 'capital',
        text: 'Porter Capital',
        to: ROUTES.capital,
        icon: PiggyBank,
        visible: userHasAccessToCapital(user),
      },
    ],
    [
      {
        id: 'notifications',
        text: 'Notifications',
        to: ROUTES.notifications,
        icon: Bell,
        visible: true,
      },
      {
        id: 'learn',
        text: 'Learn',
        to: ROUTES.learn,
        icon: GraduationCap,
        // The Learn page guard shares this audience.
        visible:
          !LEARN_IS_HIDDEN && isOnboardingAudience && !isOnboardingLoading,
      },
    ],
  ];

  const visibleGroups = routeGroups
    .map(group => group.filter(route => route.visible))
    .filter(group => group.length);

  return (
    <Menu
      sx={{ mt: '45px' }}
      id="menu-appbar"
      anchorEl={anchorElUser}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        style: {
          width: 288,
        },
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={Boolean(anchorElUser)}
      onClick={handleCloseDropdown}
      onClose={handleCloseDropdown}>
      <Box paddingX={2} paddingTop={1} paddingBottom={1.25}>
        <Typography variant="h5" color="text.primary">
          {user.business.name}
        </Typography>
        <Typography
          variant="caption"
          component="p"
          color="text.tertiary"
          marginTop={0.5}>
          {user.firstName} {user.lastName} · {user.email}
        </Typography>
      </Box>
      {visibleGroups.map(group => (
        <Box key={group[0].id}>
          <Divider sx={{ my: 0.75 }} />
          {group.map(route => {
            const Icon = route.icon;
            return (
              <MenuItem
                key={route.id}
                component={Link}
                to={route.to}
                sx={menuItemSx}
                onClick={() => setMobileDrawerOpen(false)}>
                <Icon {...menuIconProps} />
                {route.text}
              </MenuItem>
            );
          })}
        </Box>
      ))}
      <Divider sx={{ my: 0.75 }} />
      <MenuItem onClick={handleSignOut} sx={{ ...menuItemSx, fontWeight: 600 }}>
        <LogOut {...menuIconProps} />
        Sign Out
      </MenuItem>

      {user.allBusinesses.length > 1 && (
        <Box
          sx={{
            backgroundColor: '#F9F9F9',
            borderTop: '1px solid #EDEEEF',
            marginTop: 0.75,
            marginBottom: -1,
            paddingTop: 1.25,
            paddingBottom: 1,
          }}>
          <Typography
            variant="caption"
            component="p"
            fontWeight={600}
            color="text.secondary"
            letterSpacing="0.06em"
            paddingX={2}
            paddingBottom={0.875}>
            Your Locations
          </Typography>
          {user.allBusinesses.map(business => (
            <MenuItem
              key={business.businessId}
              sx={{ gap: 1.375, px: 2, py: 0.75 }}
              onClick={() => selectBusiness(business.businessId)}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #111111',
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  typography: 'overline',
                }}>
                {businessInitials(business.name)}
              </Box>
              <Box minWidth={0}>
                <Typography
                  variant="subtitle2"
                  component="p"
                  color="text.primary">
                  {business.name}
                </Typography>
                <Typography
                  variant="caption"
                  component="p"
                  color="text.secondary">
                  {business.city}, {business.state}
                </Typography>
              </Box>
              <ArrowLeftRight
                size={18}
                strokeWidth={1.75}
                color="#BDBDBD"
                style={{ marginLeft: 'auto' }}
              />
            </MenuItem>
          ))}
        </Box>
      )}
    </Menu>
  );
}

export default DropdownNavMenu;
